import { useState, type FormEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { Logo } from '@/components/ui/Logo'
import { FormField } from '@/components/ui/FormField'
import type { UserPrimaryRole } from '@/types'

type PickRole = 'school_admin' | 'instructor' | 'student'

const ROLE_LABELS: Record<PickRole, { short: string; desc: string }> = {
  school_admin: { short: 'Driving school', desc: 'I own or manage a school' },
  instructor:   { short: 'Instructor',     desc: 'I teach students independently or at a school' },
  student:      { short: 'Student',        desc: 'I want to track my road-test readiness' },
}

const ROLE_HOME: Record<UserPrimaryRole, string> = {
  school_admin: '/app/school',
  instructor:   '/app/instructor',
  student:      '/app/student',
}

export default function SelectRole() {
  const navigate     = useNavigate()
  const [searchParams] = useSearchParams()
  const { user, refreshProfile } = useAuth()

  const initialRole = (searchParams.get('role') as PickRole | null) ?? 'instructor'
  const [role,       setRole]       = useState<PickRole>(initialRole)
  const clearRoleAndError = (r: PickRole) => { setRole(r); setError('') }
  const [fullName,   setFullName]   = useState(user?.user_metadata?.full_name ?? '')
  const [schoolName, setSchoolName] = useState('')
  const [inviteToken, setInviteToken] = useState('')
  const [loading,    setLoading]    = useState(false)
  const [error,      setError]      = useState('')

  const validate = (): string | null => {
    if (!fullName.trim())                         return 'Your full name is required.'
    if (role === 'school_admin' && !schoolName.trim()) return 'School name is required.'
    if (role === 'student' && !inviteToken.trim()) return 'Invite code is required.'
    return null
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (loading || !user) return
    const validationError = validate()
    if (validationError) { setError(validationError); return }
    setError('')
    setLoading(true)
    try {
      // Always fetch the live user from the server to avoid stale cached session IDs
      // (critical after account deletion + re-signup with the same OAuth provider)
      const { data: { user: freshUser }, error: userFetchError } = await supabase.auth.getUser()
      if (userFetchError || !freshUser) {
        setError('Session expired. Please sign in again.')
        return
      }
      const userId    = freshUser.id
      const userEmail = freshUser.email ?? ''

      // Upsert profile — handles both: trigger already created row (update) and trigger didn't fire (insert)
      const { error: profileError } = await supabase
        .from('profiles')
        .upsert({
          id:             userId,
          email:          userEmail,
          full_name:      fullName.trim(),
          primary_role:   role,
          account_status: 'active',
        }, { onConflict: 'id' })
      if (profileError) {
        console.error('Profile upsert error:', profileError)
        setError(`Could not save your profile: ${profileError.message}`)
        return
      }

      if (role === 'school_admin') {
        const { data: school, error: schoolErr } = await supabase
          .from('schools')
          .insert({ name: schoolName.trim() })
          .select('id')
          .single()
        if (!schoolErr && school) {
          await supabase.from('school_memberships').insert({
            user_id: userId, school_id: school.id, role: 'OWNER', status: 'active',
          })
        }
      }

      if (role === 'instructor') {
        await supabase.from('instructor_profiles').insert({ user_id: userId })
      }

      if (role === 'student') {
        // Validate invite token
        const { data: inv } = await supabase
          .from('invitations')
          .select('id, instructor_id, school_id, status, expires_at')
          .eq('token_hash', await hashToken(inviteToken.trim()))
          .eq('status', 'pending')
          .single()

        if (!inv || new Date(inv.expires_at) < new Date()) {
          setError('Invite code is invalid or expired.')
          return
        }

        await supabase.from('student_profiles').insert({ user_id: userId })
        await supabase.from('instructor_student_relationships').insert({
          instructor_id: inv.instructor_id!,
          student_id:    userId,
          school_id:     inv.school_id,
          status:        'active',
          started_at:    new Date().toISOString(),
        })
        await supabase.from('invitations').update({
          status: 'accepted', accepted_at: new Date().toISOString(),
        }).eq('id', inv.id)
      }

      await refreshProfile()
      navigate(ROLE_HOME[role], { replace: true })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', padding: '24px 16px', background: '#FAF6EF',
    }}>
      <div style={{ width: '100%', maxWidth: 500 }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 22 }}>
          <Logo size={28} />
        </div>

        <div style={{
          background: '#fff', border: '1px solid rgba(27,42,74,0.08)',
          borderRadius: 18, padding: '28px 24px',
        }}>
          <div style={{ font: '600 22px Lora,serif', color: '#1B2A4A', textAlign: 'center' }}>
            Welcome to DriveTrack
          </div>
          <div style={{ font: '400 13px Inter,sans-serif', color: 'rgba(27,42,74,0.55)', textAlign: 'center', marginTop: 5, marginBottom: 20 }}>
            One more step — tell us about yourself
          </div>

          {/* Role picker */}
          <div className='g3' style={{ marginBottom: 20 }}>
            {(Object.entries(ROLE_LABELS) as [PickRole, { short: string; desc: string }][]).map(([r, info]) => (
              <div
                key={r}
                onClick={() => clearRoleAndError(r)}
                style={{
                  cursor: 'pointer', background: '#fff',
                  border: `2px solid ${role === r ? '#0F7C6C' : 'rgba(27,42,74,0.1)'}`,
                  borderRadius: 12, padding: 14,
                }}
              >
                <div style={{ font: '600 13px Inter,sans-serif', color: '#1B2A4A' }}>{info.short}</div>
                <div style={{ font: '400 11.5px/1.4 Inter,sans-serif', color: 'rgba(27,42,74,0.5)', marginTop: 3 }}>{info.desc}</div>
              </div>
            ))}
          </div>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <FormField
              label='Full name'
              value={fullName}
              onChange={e => setFullName(e.target.value)}
              placeholder='Your full name'
              required
            />
            {role === 'school_admin' && (
              <FormField
                label='School name'
                value={schoolName}
                onChange={e => setSchoolName(e.target.value)}
                placeholder='Maple Grove Driving School'
                required
              />
            )}
            {role === 'student' && (
              <FormField
                label='Invite code'
                value={inviteToken}
                onChange={e => setInviteToken(e.target.value)}
                placeholder='From your instructor'
                required
              />
            )}
            {error && <div className='banner-error'>{error}</div>}
            <button type='submit' className='btn-primary' disabled={loading || !user}>
              {loading ? 'Saving…' : 'Complete setup'}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}

async function hashToken(token: string): Promise<string> {
  const data = new TextEncoder().encode(token)
  const hash = await crypto.subtle.digest('SHA-256', data)
  return Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('')
}
